import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@material-ui/core";
import { RotationSchemaPermissions } from "../../../../Permissions";
import { AsignAgentsTableData } from "./AsignAgentTableHeaderData";
import { ColumnsFilterPagesEnum } from "../../../../Enums";
import {
  GetAllAgentByRotationSchemeServices,
  RemoveAgentToRotationSchemeServices,
  SetAgentInRotationSchemeAsActiveOrInActive,
} from "../../../../Services";
import {
  BasicTable,
  CustomPagination,
  CustomTableHeader,
  NoDataFound,
  TableEditDeleteActions,
  CustomButton,
  TableSkeleton,
  RolePinSideTab,
  CustomIconButton,
  SaveFilterModal,
  CustomPermission,
} from "../../../../Components";
import DeleteAgentDialog from "./DeleteAgentDialog/DeleteAgentDialog";
import AddAgentDialog from "./AddAgentDialog/AddAgentDialog";
import { useVerticalNav } from "../../../../Contexts/VerticalNavContext";
import { useNewLayoutShared } from "../../../../Contexts/NewLayoutSharedContext";
import EditAgentDialog from "./EditAgentDialog/EditAgentDialog";
import { returnPropsByPermissions } from "../../../../Helper";
import { useSelectedTheme, useTranslate } from "../../../../Hooks";

import useStyles from "./styles";

import { ColumnsThree, PlusIcon } from "../../../../assets/icons";

function AsignAgent({
  generateStepperActions,
  setIsAssignAgentModalOpen,
  isAssignAgentModalOpen,
  rotationSchemaId,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isPinTabOpen, setIsPinTabOpen] = useState(false);
  const { isDarkMode } = useSelector((state) => state.theme);
  const [totalCount, setTotalCount] = useState(null);

  const [activeItem, setActiveItem] = useState({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDelteDialogOpen] = useState(false);
  const [rotationAgents, setRotationAgents] = useState([]);
  const { setAlertBoxContent, setStickyBottomBox } = useVerticalNav();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 25,
  });
  const dropPagination = useRef(null);
  const stickyPagination = useRef(null);
  const onStatusChangeHandler = async (agentData, onError) => {
    const res = await SetAgentInRotationSchemeAsActiveOrInActive(
      agentData.agentId,
      rotationSchemaId,
      !agentData.isActive
    );
    if (!(res && res.status && res.status !== 200)) {
      setAlertBoxContent({
        display: true,
        variant: "success",
        title: translate(`agent-status-changed`),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    } else {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate(`failed-to-change-status`),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      onError();
    }
  };

  const [updateSelectedColumnItems, setUpdateSelectedColumnItems] = useState(
    AsignAgentsTableData()
  );
  const [inputValue, setInputValue] = useState(pagination.currentPage);
  const { filterModalData, savedFiltersCriteria } = useNewLayoutShared();
  const { translate } = useTranslate("NewAgentsRotationCriteria");

  const {
    theme: { palette },
  } = useSelectedTheme();
  const styles = useStyles();

  const getAllAgent = async (filter = {}) => {
    setIsLoading(true);
    const res = await GetAllAgentByRotationSchemeServices({
      id: rotationSchemaId,
      pageIndex: pagination.currentPage,
      pageSize: pagination.itemsPerPage,
      ...filter,
    });
    if (!(res && res.status && res.status !== 200)) {
      setRotationAgents([...res?.result]);
      if (!totalCount) setTotalCount(res.totalCount);
    } else setRotationAgents({ result: [], totalCount: 0 });

    setIsLoading(false);
  };

  const onDeleteAction = async () => {
    setIsLoading(true);
    const res = await RemoveAgentToRotationSchemeServices(
      rotationSchemaId,
      activeItem.agentId
    );
    if (!(res && res.status && res.status !== 200)) {
      setIsLoading(false);
      setActiveItem(null);
      setIsDelteDialogOpen(false);
      getAllAgent();
    }
  };

  useEffect(() => {
    if (rotationSchemaId) {
      getAllAgent();
    }
  }, [rotationSchemaId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!stickyPagination.current) return;
          if (entry.isIntersecting) {
            stickyPagination.current.style.display = "none";
          } else {
            stickyPagination.current.style.display = "block";
          }
        });
      },
      {
        root: null, // Default to viewport
        threshold: 1, // Adjust this threshold as needed
      }
    );

    // Start observing the dropPagination element
    if (dropPagination.current) {
      observer.observe(dropPagination.current);
    }

    // Cleanup observer on component unmount
    return () => {
      if (dropPagination.current) {
        observer.unobserve(dropPagination.current);
      }
    };
  });

  useEffect(() => {
    setStickyBottomBox(
      <CustomPermission
        permissionsList={Object.values(RotationSchemaPermissions)}
        permissionsId={
          RotationSchemaPermissions.ViewAllRotationSchema.permissionsId
        }
      >
        <CustomPagination
          hideInMobile
          ref={stickyPagination}
          currentPage={pagination.currentPage}
          inputValue={inputValue}
          setInputValue={setInputValue}
          totalItems={totalCount} ///  fix here
          itemsPerPage={pagination.itemsPerPage}
          onPageChange={(page) =>
            setPagination((prev) => ({ ...prev, currentPage: page }))
          }
          onItemsPerPageChange={(items) =>
            setPagination((prev) => ({ ...prev, itemsPerPage: items }))
          }
          isSticky
          isLoading={isLoading}
        />
      </CustomPermission>
    );

    return () => {
      setStickyBottomBox(null);
    };
  }, [isLoading]);

  return (
    <>
      <RolePinSideTab
        open={isPinTabOpen}
        onClose={() => {
          setIsPinTabOpen(!isPinTabOpen);
        }}
        allFormFields={AsignAgentsTableData(
          isDarkMode,
          onStatusChangeHandler
        ).map((column) => ({
          ...column,
        }))}
        initialTableColumnsState={[1, 2, 3, 13]}
        type={ColumnsFilterPagesEnum.assignAgent.key}
        showSearch
        setUpdateSelectedColumnItems={setUpdateSelectedColumnItems}
      />
      <Box display={"flex"} flexDirection={"column"} marginTop={"48px"}>
        <Box marginBottom={"24px"} className={styles.stepperActionContainer}>
          <div>
            <Typography
              className={styles.secondaryTitle}
              variant="h6"
              component="h2"
            >
              {translate("assign-new-agent")}
            </Typography>
            <p className={styles.subText}>
              {translate("assign-agent-form-subtitle")}
            </p>
          </div>
          <Box
            display="flex"
            flexDirection="row-reverse"
            flexBasis={"40%"}
            style={{ gap: "12px", maxHeight: "40px" }}
          >
            {generateStepperActions()}
          </Box>
        </Box>
        <Box alignSelf={"flex-end"}>
          <CustomIconButton
            variant="outlined"
            size="md"
            boxShadow="none"
            color="secondary"
            onClick={() => setIsPinTabOpen((prev) => !prev)}
          >
            <ColumnsThree
              width="20"
              height="20"
              fill={palette.button.secondary_fg}
            />
          </CustomIconButton>
        </Box>
        <Box marginTop={"24px"}>
          {isLoading ? (
            <TableSkeleton rowsNum={rotationAgents.length || 12} />
          ) : rotationAgents.length ? (
            <>
              <BasicTable
                rowHeight={84}
                setActiveItem={setActiveItem}
                setActiveItemOnRowClick={(item) => {
                  setActiveItem(item);
                }}
                rowsData={
                  rotationAgents.map((item) => {
                    return {
                      ...item,
                      id: item.agentId,
                    };
                  }) || []
                }
                columns={updateSelectedColumnItems.map((column) => ({
                  ...column,
                  renderHeader: (params) => (
                    <CustomTableHeader
                      params={params}
                      title={column.headerName}
                      hideSelect
                      isHiddenFilter={!(column.id === 1 || column.id === 5)}
                      handleFilterConfirm={(
                        params,
                        title,
                        inputValue,
                        selectedValue,
                        inputNameValue,
                        selectedNameValue
                      ) => {
                        getAllAgent({ [params.field]: inputValue });
                      }}
                    />
                  ),
                }))}
                tableActions={(data) => {
                  if (!true || Object.keys(data).length === 0) return null;
                  return (
                    <TableEditDeleteActions
                      anchorPosition={data.anchorPosition}
                      onDelete={() => setIsDelteDialogOpen(true)}
                      onEdit={() => {
                        setIsEditModalOpen(true);
                      }}
                    />
                  );
                }}
              />
              <div ref={dropPagination}>
                <CustomPagination
                  hideInMobile
                  totalItems={totalCount}
                  itemsPerPage={pagination.itemsPerPage}
                  currentPage={pagination.currentPage}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  isLoading={isLoading}
                  onPageChange={(page) =>
                    setPagination((prev) => ({ ...prev, currentPage: page }))
                  }
                  onItemsPerPageChange={(items) =>
                    setPagination((prev) => ({ ...prev, itemsPerPage: items }))
                  }
                />
              </div>
            </>
          ) : (
            <>
              <BasicTable
                rowHeight={84}
                rowsData={[]}
                columns={updateSelectedColumnItems.map((column) => ({
                  ...column,
                  renderHeader: (params) => (
                    <CustomTableHeader
                      params={params}
                      title={column.headerName}
                      hideSelect
                      isHiddenFilter={!(column.id === 1 || column.id === 5)}
                      handleFilterConfirm={(
                        params,
                        title,
                        inputValue,
                        selectedValue,
                        inputNameValue,
                        selectedNameValue
                      ) => {
                        getAllAgent({ [params.field]: inputValue });
                      }}
                    />
                  ),
                }))}
                emptyState={{ NoRowsOverlay: () => <div></div> }}
              />
              <Box
                display={"flex"}
                flexDirection={"column"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <NoDataFound
                  title={translate("no-agents-added")}
                  subTitle={translate("no-agents-subtext")}
                  wrapperClass={styles.dataNotFoundContainer}
                />
                <CustomButton
                  boxShadow="xs"
                  size="lg"
                  variant="primary"
                  onClick={() => setIsAssignAgentModalOpen(true)}
                  startIcon={
                    <PlusIcon
                      width="20"
                      height="20"
                      fill={palette.button.primary_fg}
                    />
                  }
                >
                  {translate("assign-new-agent")}
                </CustomButton>
              </Box>
            </>
          )}
        </Box>
      </Box>
      {isEditModalOpen && (
        <EditAgentDialog
          isOpen={isEditModalOpen}
          onClose={setIsEditModalOpen}
          options={{
            editValues: { ...activeItem },
            schemaId: rotationSchemaId,
            rotationAgents,
            getAllAgent,
          }}
        />
      )}
      {isDeleteDialogOpen && (
        <DeleteAgentDialog
          isOpen={isDeleteDialogOpen}
          onClose={setIsDelteDialogOpen}
          onDeleteAction={onDeleteAction}
        />
      )}
      {isAssignAgentModalOpen && (
        <AddAgentDialog
          isOpen={isAssignAgentModalOpen}
          onClose={setIsAssignAgentModalOpen}
          getAllAgent={getAllAgent}
          schemaId={rotationSchemaId}
        />
      )}
      {(filterModalData?.isOpen || savedFiltersCriteria?.isOpen) && (
        <SaveFilterModal />
      )}
    </>
  );
}

export default AsignAgent;
